import { createElement } from 'react';

import type { Tokens } from '@zeus/themes';

import { type AlertProps } from '@mui/material';

import { CoreIcon } from '../../core-icon/CoreIcon';
import { transformTypography } from '../createTypography';

type AlertVariant = Required<AlertProps>['variant'];
type AlertColor = Required<AlertProps>['color'];

function createVariantColor(
  tokens: Tokens,
  variant: AlertVariant,
  color: AlertColor
) {
  const _tokens = tokens.ui.alert[variant][color];

  return {
    '& .MuiAlert-icon': {
      color: _tokens.icon.foreground,
    },
    backgroundColor: _tokens.background,
    borderColor: _tokens.border.foreground,
    color: _tokens.text.foreground,
  };
}

function createVariant(tokens: Tokens, variant: AlertVariant) {
  return {
    borderWidth: tokens.ui.alert[variant].border.width,
    ...transformTypography(tokens.ui.alert.text.typography),
  };
}
function createVariants(tokens: Tokens) {
  return {
    filled: createVariant(tokens, 'filled'),
    filledError: createVariantColor(tokens, 'filled', 'error'),
    filledInfo: createVariantColor(tokens, 'filled', 'info'),
    filledSuccess: createVariantColor(tokens, 'filled', 'success'),
    filledWarning: createVariantColor(tokens, 'filled', 'warning'),
    outlined: createVariant(tokens, 'outlined'),
    outlinedError: createVariantColor(tokens, 'outlined', 'error'),
    outlinedInfo: createVariantColor(tokens, 'outlined', 'info'),
    outlinedSuccess: createVariantColor(tokens, 'outlined', 'success'),
    outlinedWarning: createVariantColor(tokens, 'outlined', 'warning'),
    standard: createVariant(tokens, 'standard'),
    standardError: createVariantColor(tokens, 'standard', 'error'),
    standardInfo: createVariantColor(tokens, 'standard', 'info'),
    standardSuccess: createVariantColor(tokens, 'standard', 'success'),
    standardWarning: createVariantColor(tokens, 'standard', 'warning'),
  };
}

export function createAlertStyles(tokens: Tokens): Record<string, any> {
  return {
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          error: createElement(CoreIcon, { name: 'erroroutlined' }),
          info: createElement(CoreIcon, { name: 'infooutlined' }),
          success: createElement(CoreIcon, { name: 'successoutlined' }),
          warning: createElement(CoreIcon, { name: 'warningoutlined' }),
        },
      },
      styleOverrides: {
        action: {
          paddingLeft: tokens.ui.alert.spacing.gap,
        },
        icon: {
          marginRight: tokens.ui.alert.spacing.gap,
          paddingTop: '6px',
        },
        root: {
          borderRadius: tokens.ui.alert.border.radius,
          borderStyle: 'solid',
          padding: `${tokens.ui.alert.spacing.vertical}px ${tokens.ui.alert.spacing.horizontal}px`,
        },
        ...createVariants(tokens),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: transformTypography(tokens.ui.alert.title.typography),
      },
    },
  };
}
